import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Input } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseRounds, isKarkhanaSupervisor } from "../../helpers/karkhanaEnabled";
import { addLooms, getRounds, listKarkhanaWorkTypes, saveLoomEntry } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { fmtDay, rupees, shiftDay, sortWorkTypes, todayIso } from "./rates/rateModel";
import { dropFromQueue, flushQueue, QueuedEntry, readQueue } from "./rounds/entryQueue";
import LoomSheet, { CLOSE_REASONS } from "./rounds/LoomSheet";
import { withoutRetired } from "./sarees/sareeEditModel";
import { currentPosition } from "./rounds/geo";
import "./workers/karkhana-workers.scss";
import "./rates/karkhana-rates.scss";
import "./rounds/karkhana-rounds.scss";
import { LangToggle, useKT } from "./i18n/kt";
import PageHeader, { HeaderLink } from "./ui/PageHeader";
import { cameFromInApp } from "./ui/PageHeader";
import { loomMatches } from "./home/loomCodes";
import { kp } from "./karkhanaPaths";
import { useAddressOverlay, useAddressParam, withParams } from "./ui/addressState";
import { OutsideRoundNote, roundLoomIds, RoundScopeSwitch, useRoundScope } from "./rounds/myRound";

const readAuth = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users || {};
  } catch {
    return {};
  }
};

type Filter = "ALL" | "PENDING" | "WORKED" | "CLOSED";
const FILTERS: Filter[] = ["ALL", "PENDING", "WORKED", "CLOSED"];

const KarkhanaRoundsPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const location = useLocation();
  const auth = readAuth();
  const role = auth?.role;
  const orgId: string = auth?.organizationResponse?._id || "org";
  const [date, setDate] = useState(() => new URLSearchParams(location.search).get("date") || todayIso());
  const [data, setData] = useState<any>(null);
  const [workTypes, setWorkTypes] = useState<any[]>([]);
  // Filter and search are in the address too (replaced, not pushed), so Back from a loom keeps them.
  const [filterParam, setFilter] = useAddressParam("filter", "ALL");
  const filter: Filter = (FILTERS as string[]).includes(filterParam) ? (filterParam as Filter) : "ALL";
  const [q, setQ] = useAddressParam("q");
  const [roundScope, setRoundScope] = useRoundScope();
  // The open loom lives in the address (?loom=…), so it is a real page: Back returns to the list
  // and a refresh keeps you on the loom.
  const loomSheet = useAddressOverlay("loom");
  const openId = loomSheet.value;
  const searchFor = (d: string, loomId?: string | null) =>
    withParams(history.location.search, { date: d !== todayIso() ? d : null, loom: loomId || null });
  const setOpenId = (id: string | null) => (id ? loomSheet.open(id) : loomSheet.close());
  const switchTo = (id: string) => loomSheet.switchTo(id);
  const [queue, setQueue] = useState<QueuedEntry[]>(() => readQueue(orgId));
  const [offline, setOffline] = useState(false);
  const [codes, setCodes] = useState("");
  const [tagNew, setTagNew] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async (d: string) => {
    try {
      const res: any = await getRounds(d);
      if (res?.success) {
        setData(withoutRetired(res.data));
        setOffline(false);
      } else {
        notifications({ title: "Error", message: t(res?.message || "Could not load the looms"), type: "danger" });
      }
    } catch {
      setOffline(true);
    }
  }, []);

  const flush = useCallback(async () => {
    if (!readQueue(orgId).length) return;
    const r = await flushQueue(orgId, (b) => saveLoomEntry(b));
    setQueue(readQueue(orgId));
    setOffline(r.offline);
    if (r.sent) {
      notifications({ title: t("Sent"), message: t("{{n}} saved entries sent", { n: r.sent }), type: "success" });
      load(date);
    }
  }, [orgId, date, load]);

  useEffect(() => {
    if (!canUseRounds(role)) {
      history.replace(kp.home());
      return;
    }
    listKarkhanaWorkTypes(0, 200)
      .then((res: any) => setWorkTypes(sortWorkTypes(res?.success ? res.data?.docs || [] : [])))
      .catch(() => undefined);
  }, [role, history]);

  useEffect(() => {
    load(date);
    history.replace({ ...history.location, search: searchFor(date, new URLSearchParams(history.location.search).get("loom")) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, load, history]);

  // Send anything saved without signal as soon as the phone is back online, and every 30 s.
  useEffect(() => {
    flush();
    const onOnline = () => flush();
    window.addEventListener("online", onOnline);
    const t = window.setInterval(flush, 30000);
    return () => {
      window.removeEventListener("online", onOnline);
      window.clearInterval(t);
    };
  }, [flush]);

  const looms: any[] = useMemo(() => data?.looms || [], [data]);
  const mine = useMemo(() => roundLoomIds(data), [data]);
  // A supervisor's usual round: the server sends those looms first; My round shows only them.
  const scopedLooms = mine && roundScope === "MINE" ? looms.filter((l) => mine.has(l.id)) : looms;
  const shown = scopedLooms.filter(
    (l) => (filter === "ALL" || l.state === filter) && loomMatches(l, q)
  );
  const groups = useMemo(() => {
    const m = new Map<string, any[]>();
    shown.forEach((l) => m.set(l.loomGroup || "", [...(m.get(l.loomGroup || "") || []), l]));
    return Array.from(m.entries());
  }, [shown]);
  const open = looms.find((l) => l.id === openId) || null;
  const queuedCodes = new Set(queue.filter((x) => (x.body as any).date === date).map((x) => x.loomCode));

  const onSaved = async ({ advance, queued }: { advance: boolean; queued?: boolean }) => {
    setQueue(readQueue(orgId));
    const current = openId;
    if (!queued) await load(date);
    if (!advance) {
      if (queued) closeLoom();
      return;
    }
    // Next loom after this one that still needs an entry today.
    const idx = looms.findIndex((l) => l.id === current);
    const next = [...looms.slice(idx + 1), ...looms.slice(0, idx)].find(
      (l) => l.state === "PENDING" && l.assignment && !queuedCodes.has(l.code)
    );
    if (next) switchTo(next.id);
    else closeLoom();
    if (!next) notifications({ title: t("Round done"), message: t("No more pending looms with a saree"), type: "success" });
  };

  /** Back to the list: the browser's back when we came from it, else straight to the list. */
  const closeLoom = () => {
    if (cameFromInApp(location.key)) history.goBack();
    else setOpenId(null);
  };

  const addLoomCodes = async () => {
    setAdding(true);
    try {
      let where = {};
      if (tagNew) {
        try {
          where = await currentPosition();
        } catch (e: any) {
          notifications({ title: t("Added without location"), message: t(e?.message || "Couldn't get a location"), type: "warning" });
        }
      }
      const res: any = await addLooms({ codes, ...where });
      if (!res?.success) {
        notifications({ title: "Error", message: t(res?.message || "Could not add looms"), type: "danger" });
        return;
      }
      notifications({ title: t("Looms added"), message: t("{{n}} added", { n: res.data.added }) + (res.data.skipped?.length ? ", " + t("{{n}} already there", { n: res.data.skipped.length }) : ""), type: "success" });
      setCodes("");
      setShowAdd(false);
      load(date);
    } catch {
      notifications({ title: t("Error"), message: t("Could not reach the server"), type: "danger" });
    } finally {
      setAdding(false);
    }
  };

  if (!canUseRounds(role)) return null;
  const counts =
    mine && roundScope === "MINE"
      ? {
          total: scopedLooms.length,
          pending: scopedLooms.filter((l) => l.state === "PENDING").length,
          worked: scopedLooms.filter((l) => l.state === "WORKED").length,
          closed: scopedLooms.filter((l) => l.state === "CLOSED").length,
        }
      : data?.counts || { worked: 0, pending: 0, closed: 0, total: 0 };
  const isToday = date === todayIso();

  if (open) {
    const idx = looms.findIndex((l) => l.id === open.id);
    const prev = idx > 0 ? looms[idx - 1] : null;
    const next = idx >= 0 && idx < looms.length - 1 ? looms[idx + 1] : null;
    return (
      <div className="gi-registry-page kw-page kr-rounds kr-rounds--loom">
        <PageHeader
          title={
            <>
              {t("Daily work")}
              <small>{fmtDay(date)}</small>
            </>
          }
          back={kp.dailyWork({ date: date !== todayIso() ? date : null })}
          backLabel={t("Back")}
        >
          <LangToggle />
        </PageHeader>
        <LoomSheet
          key={open.id + date}
          asPage
          loom={open}
          date={date}
          late={!!data?.late}
          workTypes={workTypes}
          orgId={orgId}
          prevLoom={prev ? { id: prev.id, code: prev.code } : null}
          nextLoom={next ? { id: next.id, code: next.code } : null}
          onGoTo={switchTo}
          onClose={closeLoom}
          onSaved={onSaved}
        />
      </div>
    );
  }

  return (
    <div className="gi-registry-page kw-page kr-rounds">
      <PageHeader title={t("Rounds")} back={isKarkhanaSupervisor(role) ? undefined : kp.dailyWork()} backLabel={t("Back")}>
        <LangToggle />
        <HeaderLink to={kp.attendance()}>{t("Attendance")}</HeaderLink>
        <HeaderLink to={kp.approvals()}>{isKarkhanaSupervisor(role) ? t("Waiting") : t("Approvals")}</HeaderLink>
      </PageHeader>

      {mine ? <RoundScopeSwitch kind="kr" scope={roundScope} onChange={setRoundScope} count={mine.size} /> : null}
      {mine && roundScope === "MINE" ? (
        <OutsideRoundNote pending={looms.filter((l) => !mine.has(l.id) && l.state === "PENDING").length} onShowAll={() => setRoundScope("ALL")} />
      ) : null}
      <div className="kr-daybar">
        <div className="kr-daybar__nav">
          <button type="button" className="kr-iconbtn" onClick={() => setDate(shiftDay(date, -1))} aria-label="Previous day">
            ‹
          </button>
          <input type="date" value={date} max={todayIso()} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Day" />
          <button type="button" className="kr-iconbtn" disabled={isToday} onClick={() => setDate(shiftDay(date, 1))} aria-label="Next day">
            ›
          </button>
          {!isToday ? (
            <button type="button" className="kr-iconbtn" onClick={() => setDate(todayIso())}>
              {t("Today")}
            </button>
          ) : null}
        </div>
        <div className="kr-counts" role="group" aria-label="Show">
          {([
            ["ALL", `${t("All")} ${counts.total}`],
            ["PENDING", `${t("To do")} ${counts.pending}`],
            ["WORKED", `${t("Done")} ${counts.worked}`],
            ["CLOSED", `${t("Closed")} ${counts.closed}`],
          ] as [Filter, string][]).map(([k, label]) => (
            <button key={k} type="button" className={`kr-choice${filter === k ? " is-on" : ""}`} onClick={() => setFilter(k)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {offline ? <div className="kr-banner is-warn">{t("No connection. You can keep entering work; it is sent when the signal is back.")}</div> : null}
      {queue.length ? (
        <div className="kr-banner is-info">
          {t("{{n}} entries saved on this phone, not sent yet.", { n: queue.length })}{" "}
          <button type="button" className="btn btn-link btn-sm p-0" onClick={flush}>
            {t("Send now")}
          </button>
          {queue.filter((x) => x.error).map((x) => (
            <div key={x.clientRequestId} className="mt-1">
              <strong>{x.loomCode}</strong>: {t(x.error || "")}{" "}
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => {
                  dropFromQueue(orgId, x.clientRequestId);
                  setQueue(readQueue(orgId));
                }}
              >
                {t("Discard")}
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {data?.late ? <div className="kr-banner is-warn">{t("{{day}} is 3 or more days back. Entries for it wait for the admin's approval.", { day: fmtDay(date) })}</div> : null}

      {data && (looms.length === 0 || showAdd) ? (
        <div className="kw-card">
          <h3>{looms.length ? t("Add more looms") : t("Add your looms")}</h3>
          <p className="kw-lede">
            {t("Type codes or ranges, e.g.")} <span className="kw-code">N01-N44, M01-M15, R01-R12</span>. {t("Codes already added are skipped.")}
          </p>
          <div className="kw-toolbar">
            <Input value={codes} onChange={(e) => setCodes(e.target.value)} placeholder="N01-N44, M01-M15" aria-label="Loom codes" autoCapitalize="characters" />
            <Button className="ResetBtn" disabled={!codes.trim() || adding} onClick={addLoomCodes}>
              {adding ? t("Adding…") : t("Add looms")}
            </Button>
          </div>
          <label className="kw-muted" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
            <input type="checkbox" checked={tagNew} onChange={(e) => setTagNew(e.target.checked)} />
            {t("Tag them with where I'm standing (stand in that shed). Each loom can be re-tagged from its sheet.")}
          </label>
        </div>
      ) : null}

      {looms.length > 12 ? (
        <Input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("Find a loom, e.g. N05")}
          aria-label="Find a loom"
          autoCapitalize="characters"
          style={{ marginBottom: 8 }}
        />
      ) : null}

      {!data ? <div className="kw-empty">{t("Loading…")}</div> : null}

      {groups.map(([group, list]) => (
        <section key={group || "none"}>
          {group ? <h3 className="kr-group-title">{group}</h3> : null}
          {list.map((l) => {
            const a = l.assignment;
            const entries = (l.entries || []).filter((e: any) => e.status === "ACCEPTED" || e.status === "PENDING_APPROVAL");
            const total = entries.reduce((s: number, e: any) => s + Number(e.calc?.total || 0), 0);
            const queued = queuedCodes.has(l.code);
            return (
              <button key={l.id} type="button" className={`kr-loom is-${l.state}`} onClick={() => setOpenId(l.id)}>
                <span className="kr-loom__code">{l.code}</span>
                <span className="kr-loom__main">
                  {a ? (
                    <>
                      <span className="kr-loom__line">
                        <span className="kw-code">{a.sareeNumber}</span> · {a.designNumber}
                      </span>
                      <span className="kr-loom__line kw-muted">
                        {a.weaver?.code} {a.weaver?.name}
                        {a.helperMode === "WITH_HELPER" ? ` + ${a.helper?.code}` : ` · ${t("alone")}`}
                      </span>
                    </>
                  ) : l.laterSaree ? (
                    <span className="kw-muted">
                      {t(l.laterSaree.moveStart?.canMove ? "Saree {{n}} is on it from {{day}} · tap to move its start" : "Saree {{n}} is on it from {{day}}", {
                        n: l.laterSaree.sareeNumber,
                        day: fmtDay(l.laterSaree.moveStart?.currentStart || l.laterSaree.from),
                      })}
                    </span>
                  ) : (
                    <span className="kw-muted">{t("No saree — tap to start one")}</span>
                  )}
                  {entries.length ? (
                    <span className="kr-loom__line">
                      {entries.map((e: any) => `${e.workTypeCode} ${e.inches} in`).join(" · ")}
                      {total ? ` · ${rupees(total, 0)}` : ""}
                    </span>
                  ) : null}
                </span>
                <span className="kr-loom__state">
                  {queued ? (
                    <span className="kw-chip is-info">{t("On phone")}</span>
                  ) : l.state === "WORKED" ? (
                    entries.some((e: any) => e.status === "PENDING_APPROVAL") ? (
                      <span className="kw-chip is-review">{t("Waiting")}</span>
                    ) : (
                      <span className="kw-chip is-ok">{t("Done")}</span>
                    )
                  ) : l.state === "CLOSED" ? (
                    <span className="kw-chip">
                      {t(CLOSE_REASONS.find((r) => r.key === l.closure?.reason)?.label || "Closed")}
                      {l.closure?.from && l.closure.from < date ? ` ${t("since {{day}}", { day: fmtDay(l.closure.from) })}` : ""}
                    </span>
                  ) : (
                    <span className="kw-chip is-review">{t("To do")}</span>
                  )}
                </span>
              </button>
            );
          })}
        </section>
      ))}

      {data && looms.length > 0 && !showAdd ? (
        <button type="button" className="btn btn-link" onClick={() => setShowAdd(true)}>
          + {t("Add looms")}
        </button>
      ) : null}

    </div>
  );
};

export default KarkhanaRoundsPage;
